import { extendPromise } from '@scayle/storefront-nuxt'
import { computed } from 'vue'
import type { LocationQuery } from 'vue-router'
import { useFilters } from '#storefront/composables'
import { useRoute } from '#app/composables/router'
import type { FilterItemWithValues } from '../types/filter'
import { DIRECT_ORDER_EXCLUDE_FILTER } from '../constants/search'
import type { RangeTuple } from '~/components/ui/sliders/RangeSlider.vue'
import { INCLUDED_FILTERS } from '../constants/filters'
import { withWhitelistedQuickFilters } from '../utils/filter'

export function useFilterNew(
  options: { immediate?: boolean; excludeDirectOrder?: boolean } = {},
  key = 'useFilter',
  category?: string,
) {
  const route = useRoute()
  const router = useRouter()

  const areFiltersUpdated = ref(false)
  const areFiltersCleared = ref(false)
  const { immediate = true, excludeDirectOrder = false } = options

  const { appliedFilter, appliedFiltersCount } = useAppliedFilters()

  const plpPath = computed(() => {
    if (Array.isArray(route.params.slug)) {
      return `/${route.params.slug.join('/')}`
    }
    return `/${route.params.slug}`
  })

  // ECOM-2639: when the consumer hides direct-order products (PLPs), the
  // filter/facet requests must exclude them from the same product universe as
  // the listing query — otherwise available filters and counts are computed over
  // products that never render. Opt-in so full search (`useGeneralSearch`) keeps
  // direct-order products per requirements.
  const directOrderExclusion = computed(() =>
    excludeDirectOrder
      ? { whitelistAttributes: [DIRECT_ORDER_EXCLUDE_FILTER] }
      : {},
  )

  // Filtered facets must use the same product universe as the listing query:
  // route included quick filters (`sale`) through `whitelistAttributes` so SAPI's
  // attribute sanitizer keeps them, then append the direct-order exclusion onto
  // that same list. Both share `whitelistAttributes`, so they are concatenated
  // rather than overwriting one another.
  const filteredWhere = computed(() => {
    const where = withWhitelistedQuickFilters(
      appliedFilter.value,
      INCLUDED_FILTERS,
    )

    if (!excludeDirectOrder) {
      return where
    }

    return {
      ...where,
      whitelistAttributes: [
        ...(where.whitelistAttributes ?? []),
        DIRECT_ORDER_EXCLUDE_FILTER,
      ],
    }
  })

  const filterData = useFilters(
    {
      params: computed(() => ({
        category: category || plpPath.value,
        where: filteredWhere.value,
        includeSellableForFree: true,
        includeSoldOut: false,
      })),
      options: { immediate, watch: [] },
    },
    key,
  )

  const unfilteredFilterData = useFilters(
    {
      params: computed(() => ({
        category: plpPath.value,
        where: {
          ...directOrderExclusion.value,
        },
        includeSellableForFree: true,
        includeSoldOut: false,
      })),
      options: { immediate, watch: [] },
    },
    `${key}-unfiltered`,
  )

  const { data, refresh: refreshFilters, status: filterStatus } = filterData
  const fetchFilters = () => refreshFilters()

  const availableFilters = computed<FilterItemWithValues[]>(
    () =>
      data.value?.filters.filter((filter) => {
        if (filter.type !== 'boolean') return true

        const trueValue = filter.values.find(
          (value) =>
            'name' in value && typeof value.name === 'boolean' && value.name,
        )

        if (!trueValue) return false

        return trueValue.productCount !== 0
      }) as FilterItemWithValues[],
  )

  const createNewAttributeQuery = (
    slug: string,
    ids?: number[],
  ): LocationQuery => {
    const attributeFilterQuery: LocationQuery = {}
    const query = { ...route.query }
    const attribute = appliedFilter.value.attributes?.find(
      (attribute) => attribute.key === slug,
    )

    if (!ids || !ids.length) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete query[slug]
      return query
    }

    const key = slug

    if (!attribute) {
      attributeFilterQuery[key] = `${ids.join(',')}`
      return { ...query, ...attributeFilterQuery }
    }

    if (attribute?.type !== 'attributes') {
      return query
    }

    if (ids.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete query[key]
      return query
    }

    attributeFilterQuery[key] = `${ids.join(',')}`

    return { ...query, ...attributeFilterQuery }
  }

  const createNewPriceQuery = (prices: RangeTuple): LocationQuery => {
    const priceFilterQuery: Record<string, string> = {}
    const query = { ...route.query }

    if (appliedFilter.value.minPrice !== prices[0]) {
      priceFilterQuery['minPrice'] = prices[0].toString()
    }

    if (appliedFilter.value.maxPrice !== prices[1]) {
      priceFilterQuery['maxPrice'] = prices[1].toString()
    }

    return { ...query, ...priceFilterQuery }
  }

  const createNewBoolAttributeQuery = (
    slug: string,
    value: boolean,
  ): LocationQuery => {
    const booleanFilterQuery: Record<string, string> = {}
    const query = { ...route.query }

    const attribute = appliedFilter.value.attributes?.find(
      (attribute) => attribute.key === slug,
    )
    const key = slug

    if (!attribute) {
      if (!value) {
        return query
      }

      booleanFilterQuery[key] = String(value)
      return { ...query, ...booleanFilterQuery }
    }

    if (attribute.type !== 'boolean') {
      return query
    }

    if (!value) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete query[key]
      return query
    }

    booleanFilterQuery[key] = String(value)
    return { ...query, ...booleanFilterQuery }
  }

  /**
   *
   * @param slug filter slug (example: `colorRange`)
   * @param id filter ids (example: [123332, 111233])
   */
  const applyAttributeFilter = async (slug: string, id: number | number[]) => {
    const ids = Array.isArray(id) ? id : [id]
    const filters = createNewAttributeQuery(slug, ids)
    await applyFilters(filters)
  }

  const applyBooleanFilter = async (slug: string, value: boolean) => {
    const filters = createNewBoolAttributeQuery(slug, value)
    await applyFilters(filters)
  }

  const applyPriceFilter = async (prices: RangeTuple) => {
    const filters = createNewPriceQuery(prices)
    await applyFilters(filters)
  }

  const resetFilter = async (key: string) => {
    const query = { ...route.query }
    const filterKey = key

    if (!query[filterKey]) {
      return
    }

    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete query[filterKey]
    await applyFilters(query)
  }

  const resetPriceFilter = async () => {
    const query = { ...route.query }

    if (!query['minPrice'] && !query['maxPrice']) {
      return
    }

    delete query['minPrice']
    delete query['maxPrice']

    await applyFilters(query)
  }

  const resetFilters = async () => {
    await applyFilters({})
    areFiltersCleared.value = true
    areFiltersUpdated.value = false

    setTimeout(() => {
      areFiltersCleared.value = false
    }, 3000)
  }

  const applyFilters = async (filter: LocationQuery, scrollToTop = true) => {
    const newQuery = {
      sort: route.query.sort,
      term: route.query.term,
      ...filter,
    }

    if ('page' in newQuery) {
      delete newQuery.page
    }

    // Should not apply reset all filter if appliedFilter is empty
    if (!appliedFiltersCount.value && !Object.keys(filter).length) {
      return
    }

    await router.push({ query: { ...newQuery } })

    areFiltersUpdated.value = true

    if (scrollToTop) window.scroll({ behavior: 'smooth', top: 0 })
  }

  const unfilteredCount = computed(() => {
    return areFiltersUpdated.value ? data.value?.unfilteredCount : 0
  })

  return extendPromise(filterData, {
    resetFilter,
    resetFilters,
    resetPriceFilter,
    applyFilters,
    applyAttributeFilter,
    applyPriceFilter,
    applyBooleanFilter,
    availableFilters,
    filterStatus,
    unfilteredCount,
    fetchFilters,
    areFiltersCleared,

    unfilteredFilters: computed(
      () => unfilteredFilterData.data.value?.filters as FilterItemWithValues[],
    ),

    fetch: async () => {
      await fetchFilters()
      return unfilteredFilterData.refresh()
    },

    refresh: () => {
      fetchFilters()
    },
  })
}
